/**
 * V527: 后台静默刷新服务
 * 
 * 功能：
 * - 页面可见时，每 30 秒检查一次缓存
 * - 对即将过期（<5 分钟）的热数据和温数据进行后台刷新
 * - 用户无感知，下次访问时已是最新数据
 * 
 * 刷新策略：
 * - 只对热数据（近 7 日）和温数据（8-30 日）进行静默刷新
 * - 冷数据（30 日外）使用物化视图，T+1 更新，无需静默刷新
 */

import { statsCache, getTierForDate, TIER_CONFIGS } from '../utils/statsCache';
import { statsDataService } from '../modules/stats/dataService';

interface CacheMetadata {
  key: string;
  timestamp: number;
  tier: 'hot' | 'warm' | 'cold';
  ttl: number;
}

export class SilentRefreshService {
  private static instance: SilentRefreshService;
  private intervalId: number | null = null;
  
  // 配置
  private readonly CHECK_INTERVAL = 30000; // 30 秒
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 分钟
  
  // 防止重复刷新
  private refreshingKeys = new Set<string>();
  
  public static getInstance(): SilentRefreshService {
    if (!SilentRefreshService.instance) {
      SilentRefreshService.instance = new SilentRefreshService();
    }
    return SilentRefreshService.instance;
  }
  
  /**
   * 启动静默刷新服务
   */
  public start(): void {
    if (this.intervalId) {
      console.log('[V527-SilentRefresh] 服务已在运行');
      return;
    }
    
    console.log('[V527-SilentRefresh] 启动后台静默刷新服务');
    
    this.intervalId = window.setInterval(() => {
      if (!document.hidden) {
        // 只在页面可见时执行
        this.checkAndRefresh();
      }
    }, this.CHECK_INTERVAL);
  }
  
  /**
   * 停止静默刷新服务
   */
  public stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[V527-SilentRefresh] 服务已停止');
    }
  }
  
  /**
   * 检查并刷新即将过期的缓存
   */
  private async checkAndRefresh(): Promise<void> {
    try {
      const keys = await this.getAllCacheKeys();
      
      for (const key of keys) {
        const shouldRefresh = await this.shouldRefreshKey(key);
        if (shouldRefresh && !this.refreshingKeys.has(key)) {
          this.refreshInBackground(key);
        }
      }
    } catch (error) {
      console.warn('[V527-SilentRefresh] 检查失败:', error);
    }
  }
  
  /**
   * 获取所有缓存键
   */
  private async getAllCacheKeys(): Promise<string[]> {
    try {
      // 从 localStorage 获取所有缓存键
      const keys: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('stats_') || key.startsWith('player_stats:'))) {
          keys.push(key);
        }
      }
      
      return keys;
    } catch (error) {
      console.warn('[V527-SilentRefresh] 获取缓存键失败:', error);
      return [];
    }
  }
  
  /**
   * 判断是否需要刷新某个缓存键
   */
  private async shouldRefreshKey(key: string): Promise<boolean> {
    try {
      const item = localStorage.getItem(key);
      if (!item) return false;
      
      const entry = JSON.parse(item);
      if (!entry || !entry.timestamp || !entry.data) return false;
      
      // 提取日期信息
      const dateStr = this.extractDateFromKey(key);
      if (!dateStr) return false;
      
      // 获取数据层级
      const tier = getTierForDate(dateStr);
      
      // 冷数据不静默刷新（物化视图 T+1 更新）
      if (tier === 'cold') {
        return false;
      }
      
      // 计算剩余 TTL
      const config = TIER_CONFIGS[tier];
      const expiresAt = entry.timestamp + config.ttl;
      const timeToExpiry = expiresAt - Date.now();
      
      // 即将过期但未过期，且是热数据或温数据
      const shouldRefresh = timeToExpiry > 0 && timeToExpiry < this.REFRESH_THRESHOLD;
      
      if (shouldRefresh) {
        console.log(`[V527-SilentRefresh] ${key} 即将过期 (${Math.floor(timeToExpiry / 1000)}s)，准备刷新`);
      }
      
      return shouldRefresh;
    } catch (error) {
      console.warn('[V527-SilentRefresh] 判断是否刷新失败:', key, error);
      return false;
    }
  }
  
  /**
   * 从缓存键中提取日期
   */
  private extractDateFromKey(key: string): string | null {
    // 匹配格式：stats_overview_2026-05-04 或 player_stats:all:all
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1];
    }
    
    // 如果没有明确日期，默认返回今天（热数据）
    return new Date().toISOString().split('T')[0];
  }
  
  /**
   * 后台静默刷新
   */
  private async refreshInBackground(key: string): Promise<void> {
    this.refreshingKeys.add(key);
    
    try {
      console.log(`[V527-SilentRefresh] 开始后台刷新：${key}`);
      
      // 根据 key 类型调用不同的 API
      let newData: any = null;
      
      if (key.startsWith('stats_overview_')) {
        const dateStr = this.extractDateFromKey(key);
        if (dateStr) {
          // 调用总览统计 API
          newData = await statsDataService.loadPlayerStatsWithDateRange(
            undefined,
            undefined,
            new Date(dateStr),
            new Date(dateStr)
          );
        }
      } else if (key.startsWith('player_stats:')) {
        // 解析过滤器
        const parts = key.split(':');
        const dojoFilter = parts[1] || 'all';
        const seasonFilter = parts[2] || 'all';
        
        newData = await statsDataService.loadPlayerStats(dojoFilter, seasonFilter);
      }
      
      if (newData) {
        // 更新缓存
        const dateStr = this.extractDateFromKey(key);
        await statsCache.set(key, newData, dateStr || undefined);
        console.log(`[V527-SilentRefresh] 刷新成功：${key}`);
      }
    } catch (error) {
      console.warn(`[V527-SilentRefresh] 刷新失败：${key}`, error);
      // 保持旧缓存，不通知用户
    } finally {
      this.refreshingKeys.delete(key);
    }
  }
}
